import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TablerIconsModule } from 'angular-tabler-icons';
import { MaterialModule } from 'src/app/material.module';
import { LookupService } from 'src/app/services/lookup.service';
import { CommonDropDown } from 'src/app/common/types/dropdown.type';
import { CategoryType } from 'src/app/common/enums/category-type';
import { Subject, forkJoin, takeUntil } from 'rxjs';
import { ClinicalItemService } from 'src/app/pages/category/clinical-item/clinical-item.service';
import { IClinicalItem } from 'src/app/pages/category/clinical-item/iclinical-item';

interface CategoryWithSubcategories extends CommonDropDown {
  subcategories: SubcategoryWithItems[];
}

interface SubcategoryWithItems {
  id: number;
  name: string;
  items: IClinicalItem[];
}

@Component({
  selector: 'app-suggested-investigation-popup',
  standalone: true,
  imports: [
    CommonModule,
    MaterialModule,
    TranslateModule,
    TablerIconsModule
  ],
  templateUrl: './suggested-investigation-popup.component.html',
  styleUrl: './suggested-investigation-popup.component.scss'
})
export class SuggestedInvestigationPopupComponent implements OnInit, OnDestroy {
  categories: CategoryWithSubcategories[] = [];
  selectedCategoryIndex: number = 0;
  selectedItems: Set<number> = new Set<number>(); // Store by ID for independent selection
  private _preselectedDescriptions: Set<string> = new Set<string>(); // Store initial descriptions for matching
  expandedSubcategories: Set<number> = new Set<number>();
  searchTerm = '';
  isLoading = false;

  private _unsubscribeAll: Subject<void> = new Subject<void>();

  constructor(
    private _dialogRef: MatDialogRef<SuggestedInvestigationPopupComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { existingInvestigation?: string },
    private _lookupService: LookupService,
    private _clinicalItemService: ClinicalItemService,
    private _cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.parseExistingInvestigation();
    this.loadCategories();
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  private loadCategories(): void {
    this.isLoading = true;
    this._lookupService.getCategoryByType(CategoryType.SuggestedInvestigation)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: (categories) => {
          if (categories && categories.length > 0) {
            this.loadSubcategoriesAndItems(categories);
          } else {
            this.isLoading = false;
          }
        },
        error: (error) => {
          console.error('Error loading categories:', error);
          this.isLoading = false;
        }
      });
  }

  private loadSubcategoriesAndItems(categories: CommonDropDown[]): void {
    const categoryRequests = categories.map(category =>
      this._lookupService.getSubCategory(category.id).pipe(
        takeUntil(this._unsubscribeAll)
      )
    );

    forkJoin(categoryRequests).subscribe({
      next: (subcategoryArrays) => {
        const categoriesWithSubcategories: CategoryWithSubcategories[] = [];

        categories.forEach((category, categoryIndex) => {
          const subcategories = subcategoryArrays[categoryIndex] || [];
          const subcategoryWithItems: SubcategoryWithItems[] = subcategories.map(sub => ({
            id: sub.id,
            name: sub.name || '',
            items: []
          }));

          categoriesWithSubcategories.push({
            ...category,
            subcategories: subcategoryWithItems
          });
        });

        this.categories = categoriesWithSubcategories;

        // Load items for all subcategories
        this.loadItemsForAllSubcategories();

        // Set first category as selected
        this.selectedCategoryIndex = 0;
      },
      error: (error) => {
        console.error('Error loading subcategories:', error);
        this.isLoading = false;
      }
    });
  }

  private loadItemsForAllSubcategories(): void {
    const itemRequests: any[] = [];
    const requestMap: { categoryIndex: number; subcategoryIndex: number; isGeneral: boolean }[] = [];

    this.categories.forEach((category, categoryIndex) => {
      // Load general items (subcategoryID = null) for this category first
      requestMap.push({ categoryIndex, subcategoryIndex: -1, isGeneral: true });
      itemRequests.push(
        this._clinicalItemService.getClinicalItem({
          pageNumber: 0,
          pageSize: 100,
          isDescending: false,
          categoryId: category.id,
          subcategoryID: null
        }).pipe(takeUntil(this._unsubscribeAll))
      );

      // Load items for each subcategory
      category.subcategories.forEach((subcategory, subcategoryIndex) => {
        requestMap.push({ categoryIndex, subcategoryIndex, isGeneral: false });
        itemRequests.push(
          this._clinicalItemService.getClinicalItem({
            pageNumber: 0,
            pageSize: 100,
            isDescending: false,
            categoryId: category.id,
            subcategoryID: subcategory.id
          }).pipe(takeUntil(this._unsubscribeAll))
        );
      });
    });

    if (itemRequests.length === 0) {
      this.isLoading = false;
      return;
    }

    forkJoin(itemRequests).subscribe({
      next: (responses) => {
        const generalItemsMap: { [categoryIndex: number]: IClinicalItem[] } = {};

        // Process responses
        requestMap.forEach((map, index) => {
          const items = responses[index]?.result?.clinicalItems || [];

          if (map.isGeneral) {
            // Store general items for this category
            generalItemsMap[map.categoryIndex] = items;
          } else {
            // Assign items to specific subcategory
            const category = this.categories[map.categoryIndex];
            const subcategory = category.subcategories[map.subcategoryIndex];
            subcategory.items = items;
          }
        });

        // Add general items as a virtual subcategory if they exist
        Object.keys(generalItemsMap).forEach(categoryIndexStr => {
          const categoryIndex = parseInt(categoryIndexStr);
          const generalItems = generalItemsMap[categoryIndex];

          if (generalItems && generalItems.length > 0) {
            const category = this.categories[categoryIndex];
            // Check if "General" or "Others" subcategory already exists
            const existingGeneral = category.subcategories.find(sub =>
              sub.name?.toLowerCase() === 'general' ||
              sub.name?.toLowerCase() === 'others'
            );

            if (existingGeneral) {
              // Add to existing general subcategory
              existingGeneral.items = [...existingGeneral.items, ...generalItems];
            } else {
              // Create virtual "General" subcategory
              const generalSubcategory: SubcategoryWithItems = {
                id: -999, // Virtual ID
                name: 'General',
                items: generalItems
              };
              category.subcategories.push(generalSubcategory);
            }
          }
        });

        this.isLoading = false;
        // Use setTimeout to defer state changes to next change detection cycle
        setTimeout(() => {
          this.restoreSelectedItems();
          // Always expand first subcategory of all categories
          this.categories.forEach((_, index) => {
            this.expandFirstSubcategoryOfCategory(index);
          });
          this._cdr.detectChanges();
        }, 0);
      },
      error: (error) => {
        console.error('Error loading items:', error);
        this.isLoading = false;
      }
    });
  }

  private parseExistingInvestigation(): void {
    if (!this.data?.existingInvestigation) {
      return;
    }

    const existingText = this.data.existingInvestigation;
    const items = existingText.split(',').map(item => item.trim()).filter(item => item.length > 0);

    items.forEach(item => {
      this._preselectedDescriptions.add(item.toLowerCase());
    });
  }

  private restoreSelectedItems(): void {
    this.categories.forEach(category => {
      category.subcategories.forEach(subcategory => {
        subcategory.items.forEach(item => {
          const itemDescription = item.description?.toLowerCase() || '';
          if (this._preselectedDescriptions.has(itemDescription)) {
            this.selectedItems.add(item.id);
          }
        });

        const hasSelected = subcategory.items.some(item =>
          this.selectedItems.has(item.id)
        );
        if (hasSelected) {
          this.expandedSubcategories.add(subcategory.id);
        }
      });
    });
  }
  private expandFirstSubcategoryOfCategory(categoryIndex: number): void {
    if (categoryIndex >= 0 && categoryIndex < this.categories.length) {
      const category = this.categories[categoryIndex];
      if (category.subcategories && category.subcategories.length > 0) {
        const firstSubcategory = category.subcategories[0];
        this.expandedSubcategories.add(firstSubcategory.id);
      }
    }
  }

  onCategoryChange(index: number): void {
    this.selectedCategoryIndex = index;
    // Always expand first subcategory when switching to any tab
    this.expandFirstSubcategoryOfCategory(index);
  }

  selectCategory(index: number): void {
    this.selectedCategoryIndex = index;
  }

  onSearchChange(value: string): void {
    this.searchTerm = value;
  }

  onSubcategoryToggle(subcategoryId: number, isOpen: boolean): void {
    if (isOpen) {
      this.expandedSubcategories.add(subcategoryId);
    } else {
      this.expandedSubcategories.delete(subcategoryId);
    }
  }

  isSubcategoryExpanded(subcategoryId: number): boolean {
    return this.expandedSubcategories.has(subcategoryId);
  }

  onCheckboxChange(item: IClinicalItem, checked: boolean): void {
    if (checked) {
      this.selectedItems.add(item.id);
    } else {
      this.selectedItems.delete(item.id);
    }
  }

  isItemSelected(item: IClinicalItem): boolean {
    return this.selectedItems.has(item.id);
  }

  getSelectedCountForSubcategory(subcategory: SubcategoryWithItems): number {
    return subcategory.items.filter(item => this.isItemSelected(item)).length;
  }

  getSelectedCountForCategory(category: CategoryWithSubcategories): number {
    let count = 0;
    category.subcategories.forEach(subcategory => {
      count += this.getSelectedCountForSubcategory(subcategory);
    });
    return count;
  }

  getCurrentCategory(): CategoryWithSubcategories | undefined {
    return this.categories[this.selectedCategoryIndex];
  }

  getFilteredItems(subcategory: SubcategoryWithItems): IClinicalItem[] {
    const searchValue = this.searchTerm.trim().toLowerCase();

    if (!searchValue) {
      return subcategory.items;
    }

    const subcategoryName = subcategory.name?.toLowerCase() || '';
    if (subcategoryName.includes(searchValue)) {
      return subcategory.items;
    }

    return subcategory.items.filter(item =>
      (item.description || '').toLowerCase().includes(searchValue)
    );
  }

  getVisibleSubcategories(category: CategoryWithSubcategories): SubcategoryWithItems[] {
    return category.subcategories.filter(subcategory =>
      this.getFilteredItems(subcategory).length > 0
    );
  }

  hasVisibleItems(category: CategoryWithSubcategories): boolean {
    return this.getVisibleSubcategories(category).length > 0;
  }

  onSave(): void {
    // Get all selected item descriptions
    const selectedDescriptions = new Set<string>();
    this.categories.forEach(category => {
      category.subcategories.forEach(subcategory => {
        subcategory.items.forEach(item => {
          if (this.isItemSelected(item) && item.description) {
            selectedDescriptions.add(item.description);
          }
        });
      });
    });

    const result = Array.from(selectedDescriptions).join(', ');
    this._dialogRef.close(result);
  }

  onCancel(): void {
    this._dialogRef.close(null);
  }
}
